import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';
import './VerifyEmail.css';
import logoImage from '../../assets/icon_logo.png';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);

  const inputRefs = useRef([]);

  useEffect(() => {
    // If no email was passed, redirect to signup
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    const verificationCode = code.join('');

    if (verificationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
      if (err) {
        console.error('Verification failed:', err);
        setError(err.message || 'Verification failed');
        setLoading(false);
        return;
      }

      console.log('Email verified successfully');
      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    });
  };

  const handleResendCode = () => {
    setResending(true);
    setError(null);

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        console.error('Resend failed:', err);
        setError(err.message || 'Failed to resend code');
        setResending(false);
        return;
      }

      console.log('Verification code resent');
      alert('A new verification code has been sent to your email');
      setResending(false);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    });
  };

  if (!email) {
    return null;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logoImage} alt="CoinedIn Logo" className="auth-logo" />
          <h1>CoinedIn</h1>
          <p className="auth-tagline">Verify Your Email</p>
        </div>

        <form onSubmit={handleVerify} className="auth-form">
          <h2>Enter Verification Code</h2>
          <p className="verification-description">
            We've sent a 6-digit verification code to <strong>{email}</strong>
          </p>

          {error && (
            <div className="auth-error">
              <span className="error-icon">⚠</span>
              <p>{error}</p>
            </div>
          )}

          {success && (
            <div className="auth-success">
              <span className="success-icon">✓</span>
              <p>Email verified successfully! Redirecting to login...</p>
            </div>
          )}

          <div className="verification-input-group" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="verification-input"
                disabled={loading || success}
              />
            ))}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading || success || code.join('').length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>

          <div className="auth-links">
            <span>Didn't receive the code?</span>
            <button
              type="button"
              className="link-button"
              onClick={handleResendCode}
              disabled={resending || loading || success}
            >
              {resending ? 'Sending...' : 'Resend code'}
            </button>
          </div>

          <div className="auth-links" style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              Back to login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VerifyEmail;
